import { useEffect, useRef, useState } from 'react';
import ConsentCheckbox from './ConsentCheckbox';
import { FormControl, FormLabel, Input, Button, Alert, Typography, Stack } from '@mui/joy';
import { apiPasswordResetRequest, apiPasswordResetConfirm } from '../../auth';
import { formatPhoneRu, phoneDigitsCount } from '../../utils';

// Длина кода: почта — 6, звонок — 4 (совпадает с бэкендом)
const CODE_LEN = { email: 6, call: 4 };

/**
 * Восстановление пароля по коду (почта или звонок).
 * Шаг 1 — контакт и запрос кода; шаг 2 — код + новый пароль → сразу вход.
 * props: role ('doctor'|'patient'), onSuccess(profile), onCancel()
 */
export default function PasswordResetForm({ role, consentAccepted = false, onConsentChange, onSuccess, onCancel }) {
  const [step, setStep] = useState('request');   // 'request' | 'confirm'
  const [via, setVia] = useState('email');         // 'email' | 'phone'
  const [contact, setContact] = useState('');
  const [channel, setChannel] = useState('email'); // 'email' | 'call'
  const [masked, setMasked] = useState('');
  const [code, setCode] = useState('');
  const [pass1, setPass1] = useState('');
  const [pass2, setPass2] = useState('');
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);
  useEffect(() => {
    if (consentAccepted) setError((previous) => previous === 'Подтвердите согласие на обработку персональных данных' ? '' : previous);
  }, [consentAccepted]);

  useEffect(() => {
    if (secondsLeft <= 0) return;
    timerRef.current = setInterval(() => setSecondsLeft((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(timerRef.current);
  }, [secondsLeft]);

  const destinationValue = () => (via === 'email' ? contact.trim().toLowerCase() : contact.trim());
  const validContact = () =>
    via === 'email'
      ? /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.trim())
      : phoneDigitsCount(contact) === 11;

  const requestCode = async () => {
    setError('');
    if (loading) return;
    if (!consentAccepted) { setError('Подтвердите согласие на обработку персональных данных'); return; }
    if (!validContact()) {
      setError(via === 'email' ? 'Введите корректный email' : 'Введите телефон полностью');
      return;
    }
    const ch = via === 'email' ? 'email' : 'call';
    setChannel(ch);
    setLoading(true);
    try {
      const resp = await apiPasswordResetRequest({ consentAccepted, role, channel: ch, destination: destinationValue() });
      if (!resp?.success) { setError(resp?.error || 'Не удалось отправить код'); return; }
      setMasked(resp.destination_masked || '');
      setSecondsLeft(60);
      setStep('confirm');
    } catch (e) {
      if (e?.response?.status === 429) setError('Код уже отправлен. Повторите через минуту.');
      else setError(e?.response?.data?.error || 'Не удалось отправить код');
    } finally { setLoading(false); }
  };

  const confirm = async () => {
    setError('');
    if (loading) return;
    if (!consentAccepted) { setError('Подтвердите согласие на обработку персональных данных'); return; }
    const need = CODE_LEN[channel] || 6;
    if (code.replace(/\D/g, '').length < need) { setError(`Введите код из ${need} цифр`); return; }
    if (pass1.length < 6) { setError('Пароль должен быть не короче 6 символов'); return; }
    if (pass1 !== pass2) { setError('Пароли не совпадают'); return; }
    setLoading(true);
    try {
      const resp = await apiPasswordResetConfirm({ consentAccepted,
        role, channel, destination: destinationValue(),
        code: code.replace(/\D/g, ''), new_password: pass1,
        device_name: navigator.userAgent,
      });
      if (!resp?.success) { setError(resp?.error || 'Не удалось сохранить пароль'); return; }
      onSuccess?.(resp.profile);
    } catch (e) {
      setError(e?.response?.data?.error || 'Не удалось сохранить пароль');
    } finally { setLoading(false); }
  };

  // ── Шаг 1: контакт и запрос кода ──
  if (step === 'request') {
    return (
      <>
        {error && <Alert color="danger" variant="soft">{error}</Alert>}

        <Typography level="title-sm">Восстановление пароля</Typography>

        <Stack direction="row" spacing={1}>
          <Button fullWidth variant={via === 'email' ? 'solid' : 'soft'} onClick={() => setVia('email')}>
            По почте
          </Button>
          <Button fullWidth variant={via === 'phone' ? 'solid' : 'soft'} onClick={() => setVia('phone')}>
            По телефону
          </Button>
        </Stack>

        {via === 'email' ? (
          <FormControl>
            <FormLabel>Email аккаунта</FormLabel>
            <Input
              type="email" placeholder="you@mail.ru" value={contact}
              onChange={(e) => setContact(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && requestCode()}
            />
          </FormControl>
        ) : (
          <FormControl>
            <FormLabel>Телефон аккаунта</FormLabel>
            <Input
              type="tel" placeholder="+7 (999) 123-45-67" value={contact}
              onChange={(e) => setContact(formatPhoneRu(e.target.value, contact))}
              slotProps={{ input: { inputMode: 'tel', maxLength: 18 } }}
            />
          </FormControl>
        )}

        <Button loading={loading} disabled={!consentAccepted} onClick={requestCode} sx={{ mt: 0.5 }}>
          {via === 'email' ? 'Получить код на почту' : 'Получить код звонком'}
        </Button>
        <ConsentCheckbox checked={consentAccepted} onChange={onConsentChange} disabled={loading} />

        <Button variant="plain" color="neutral" size="sm" onClick={onCancel} sx={{ alignSelf: 'center' }}>
          Назад ко входу
        </Button>
      </>
    );
  }

  // ── Шаг 2: код + новый пароль ──
  const need = CODE_LEN[channel] || 6;
  return (
    <>
      {error && <Alert color="danger" variant="soft">{error}</Alert>}

      <Typography level="body-sm" sx={{ textAlign: 'center' }} textColor="text.tertiary">
        {channel === 'call'
          ? `Вам поступит звонок — введите последние ${need} цифр номера`
          : <>Код отправлен на <b>{masked}</b></>}
      </Typography>

      <FormControl>
        <FormLabel>Код из {need} цифр</FormLabel>
        <Input
          autoFocus placeholder={'•'.repeat(need)} value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, need))}
          slotProps={{ input: { inputMode: 'numeric', maxLength: need } }}
        />
      </FormControl>

      <FormControl>
        <FormLabel>Новый пароль</FormLabel>
        <Input type="password" placeholder="Не короче 6 символов" value={pass1}
          onChange={(e) => setPass1(e.target.value)} />
      </FormControl>

      <FormControl>
        <FormLabel>Повторите пароль</FormLabel>
        <Input type="password" placeholder="Ещё раз" value={pass2}
          onChange={(e) => setPass2(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && confirm()} />
      </FormControl>

      <Button loading={loading} disabled={!consentAccepted} onClick={confirm} sx={{ mt: 0.5 }}>
        Сохранить и войти
      </Button>
      <ConsentCheckbox checked={consentAccepted} onChange={onConsentChange} disabled={loading} />

      <Stack direction="row" spacing={1} sx={{ justifyContent: 'space-between' }}>
        <Button variant="plain" color="neutral" size="sm" onClick={() => { setStep('request'); setError(''); }}>
          Изменить контакт
        </Button>
        <Button variant="plain" size="sm" disabled={!consentAccepted || loading || secondsLeft > 0} onClick={requestCode}>
          {secondsLeft > 0 ? `Отправить снова (${secondsLeft})` : 'Отправить снова'}
        </Button>
      </Stack>
    </>
  );
}